import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { schema } from "./schema";
import { useEffect } from "react";
import { hasValueObj, isArrayWithRecords } from "../../../util/libObject";
import { BtnSubmit } from "../../button/button-submit/button-submit";
import TableInstallments from "../table/table-installments";
import LibDateTime from "../../../util/libDateTime";
import { handleMessageError, handleMessageWarning } from "../../toast";

// hideSubmit: esconde o botão "Confirmar Parcelas" e, no lugar dele, empurra as parcelas pro
// pai automaticamente sempre que mudam. Serve pra quem embute o Installment num fluxo que já
// tem botão de salvar próprio (ex: wizard de Alteração de Venda) — a validação final fica com o
// pai. Default false, então todos os usos existentes seguem com o botão, sem quebrar nada.
const TableInstallment = ({ installmentSetup, formaPgtoData, CallBackInstallments, hideSubmit = false }) => {
	const { control, handleSubmit, setValue, watch, isSubmitting } = useForm({
		defaultValues: {
			parcelas: [],
		},
		resolver: zodResolver(schema),
	});

	const valor_total = installmentSetup?.valor_total ?? 0;
	const watchedParcelas = watch("parcelas");

	// Modo sem botão próprio: espelha as parcelas atuais pro pai a cada mudança
	useEffect(() => {
		if (hideSubmit && CallBackInstallments) {
			CallBackInstallments({ parcelas: watchedParcelas });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(watchedParcelas), hideSubmit]);

	useEffect(() => {
		const generateInstallments = async () => {
			try {
				if (!hasValueObj(installmentSetup)) return;

				const newInstallments = [];

				const {
					qtde_parcelas,
					id_forma,
					primeiro_lancamento,
					cobrar_dia,
					id_tipo_repeticao,
					valor_total,
					id_conta,
				} = installmentSetup;

				const totalCentavos = Math.round(valor_total * 100);
				const valorBaseCentavos = Math.floor(totalCentavos / qtde_parcelas);
				const resto = totalCentavos - valorBaseCentavos * qtde_parcelas;

				const repetitionDays = id_tipo_repeticao;
				// Dinheiro, Pix, Débito e Crédito nascem pagos com data de hoje (mesmo padrão do close-sale-modal)
				const isAutoDate = [1, 3, 4, 5].includes(Number(id_forma));

				for (let i = 0; i < qtde_parcelas; i++) {
					const data_vencimento = new Date(primeiro_lancamento);

					if (i > 0 && !isAutoDate) {
						if (id_tipo_repeticao === 30) {
							const newDataVencimento = await validateLastDay(data_vencimento, cobrar_dia, i);
							data_vencimento.setTime(newDataVencimento.getTime());
						} else {
							data_vencimento.setDate(data_vencimento.getDate() + i * repetitionDays);
						}
					}

					const data_vencimentoFormatada = data_vencimento.toISOString().split("T")[0];

					// Distribui o "resto" nos últimos `resto` itens
					const parcelaCentavos = i >= qtde_parcelas - resto ? valorBaseCentavos + 1 : valorBaseCentavos;

					newInstallments.push({
						id: i + 1,
						parcela: i + 1,
						parcela_total: qtde_parcelas,
						data_vencimento: data_vencimentoFormatada,
						forma_pagamento: formaPgtoData.find((forma) => forma.id_forma === id_forma)?.nome || "",
						id_forma,
						id_conta,
						valor: (parcelaCentavos / 100).toFixed(2),
						ativo: isAutoDate ? false : true, // Dinheiro, Pix, Débito e Crédito nascem como "Pago"
						valor_total,
					});
				}

				setValue("parcelas", newInstallments);
			} catch (error) {
				setValue("parcelas", []);
				handleMessageError("Erro ao gerar parcelas");
			}
		};

		generateInstallments();
	}, [installmentSetup, setValue, formaPgtoData]);

	const validateLastDay = async (dataVencimento, cobrar_dia, i) => {
		// Fazendo uma cópia da data para não afetar a original
		const dataVencimentoCopia = new Date(dataVencimento.getTime());
		// Ajusta para o primeiro dia do mês para não correr o risco de quando somar 1 mes, cair em um mês com menos dias
		dataVencimentoCopia.setDate(1);
		// Adiciona a quantidade de meses
		dataVencimentoCopia.setMonth(dataVencimentoCopia.getMonth() + i);
		// Obtém o último dia do mês
		const ultimoDia = LibDateTime.getLastDayOfMonth(dataVencimentoCopia);

		// Se o último dia do mês for menor que o dia de cobrança, então retorna o último dia do mês
		if (ultimoDia < cobrar_dia) {
			dataVencimentoCopia.setDate(ultimoDia);
		} else {
			dataVencimentoCopia.setDate(cobrar_dia);
		}
		return dataVencimentoCopia; // Caso contrário, retorna o dia de cobrança
	};

	const handleOnSubmit = (data) => {
		const totalCentavos = data.parcelas.reduce((acc, parcela) => {
			return acc + Math.round(parseFloat(parcela.valor || 0) * 100);
		}, 0);

		const totalParcelas = totalCentavos / 100;

		if (!isArrayWithRecords(data.parcelas)) {
			handleMessageWarning("Gere as parcelas antes de confirmar");
			return;
		}

		if (totalParcelas !== valor_total) {
			handleMessageWarning(
				"O valor total das parcelas não corresponde ao valor total do lançamento. Corrija antes de confirmar.",
			);
			return;
		}

		//Quando faz o submit, passa o objeto com as parcelas para o componente pai
		CallBackInstallments(data);
	};

	return (
		<>
			<form onSubmit={handleSubmit(handleOnSubmit)}>
				{!hideSubmit && (
					<BtnSubmit className={"btn-sm mt-3 mb-3"} text="Confirmar Parcelas" isSubmitting={isSubmitting} />
				)}
				{/* Chama o componente de parcelas passando as parcelas geradas depois tudo é alterado na tabela  */}
				{/* e no submit manda os dados da tabela para o componente pai que chamou este form */}
				{/* <TableInstallments
					installments={generatedInstallments}
					control={control}
					setValue={setValue}
					hasSubmit={true}
				/> */}
				<TableInstallments
					installments={watch("parcelas")}
					control={control}
					setValue={setValue}
					valor_total={watch("parcelas")[0]?.valor_total || 0}
					hasSubmit={true}
				/>
			</form>
		</>
	);
};

export default TableInstallment;
